// src/context/TranscriptionContext.jsx

import React, {
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

export const TranscriptionContext = createContext();

const JOBS_STORAGE_KEY = 'jobsMap';
const LEGACY_JOB_STATE_KEY = 'jobState';
const DISMISSED_RECENT_JOBS_KEY = 'dismissedRecentJobIds';
const MAX_DISMISSED_RECENT_JOBS = 200;

const FINAL_STATUSES = ['completed', 'failed', 'canceled'];

function loadInitialJobs() {
  try {
    const stored = sessionStorage.getItem(JOBS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed && typeof parsed === 'object') return parsed;
    }
    // Migration aus altem Single-Job-State
    const legacy = sessionStorage.getItem(LEGACY_JOB_STATE_KEY);
    if (legacy) {
      const obj = JSON.parse(legacy);
      if (obj && obj.jobId) return { [obj.jobId]: obj };
    }
  } catch (e) {
    // ignore parse errors
  }
  return {};
}

function loadDismissedRecentJobs() {
  try {
    const stored = localStorage.getItem(DISMISSED_RECENT_JOBS_KEY);
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((id) => typeof id === 'string');
  } catch (e) {
    return [];
  }
}

function persistDismissedRecentJobs(ids) {
  try {
    if (ids.length > 0) {
      localStorage.setItem(
        DISMISSED_RECENT_JOBS_KEY,
        JSON.stringify(ids.slice(-MAX_DISMISSED_RECENT_JOBS)),
      );
    } else {
      localStorage.removeItem(DISMISSED_RECENT_JOBS_KEY);
    }
  } catch (e) {
    // ignore persistence errors
  }
}

export const TranscriptionProvider = ({ children }) => {
  const [jobs, setJobs] = useState(loadInitialJobs);
  const [dismissedRecentJobIds, setDismissedRecentJobIds] = useState(
    loadDismissedRecentJobs,
  );
  const [activeJobId, setActiveJobId] = useState(() => {
    try {
      const legacy = sessionStorage.getItem(LEGACY_JOB_STATE_KEY);
      if (legacy) {
        const obj = JSON.parse(legacy);
        return obj?.jobId || null;
      }
    } catch (e) {
      /* ignore */
    }
    return null;
  });

  const [transcriptionData, setTranscriptionData] = useState(() => {
    const savedData = sessionStorage.getItem('transcriptionData');
    return savedData ? JSON.parse(savedData) : null;
  });

  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    persistDismissedRecentJobs(dismissedRecentJobIds);
  }, [dismissedRecentJobIds]);

  // Always-fresh ref to the dismissed-id list. The SSE hook (useJobsStream)
  // captures the snapshot/upsert callbacks once at mount, so any callback
  // that closed over the array directly would race against later "dismiss"
  // actions and silently re-add finished jobs. Reading from a ref instead
  // means every callback invocation sees the latest list.
  const dismissedRecentJobIdsRef = useRef(dismissedRecentJobIds);
  useEffect(() => {
    dismissedRecentJobIdsRef.current = dismissedRecentJobIds;
  }, [dismissedRecentJobIds]);

  // jobs-Map persistieren
  useEffect(() => {
    if (jobs && Object.keys(jobs).length > 0) {
      sessionStorage.setItem(JOBS_STORAGE_KEY, JSON.stringify(jobs));
    } else {
      sessionStorage.removeItem(JOBS_STORAGE_KEY);
    }
  }, [jobs]);

  useEffect(() => {
    if (transcriptionData) {
      sessionStorage.setItem(
        'transcriptionData',
        JSON.stringify(transcriptionData),
      );
    } else {
      sessionStorage.removeItem('transcriptionData');
    }
  }, [transcriptionData]);

  useEffect(() => {
    if (dismissedRecentJobIds.length === 0) return;
    const dismissed = new Set(dismissedRecentJobIds);
    setJobs((prev) => {
      let changed = false;
      const next = {};
      for (const [id, job] of Object.entries(prev)) {
        if (FINAL_STATUSES.includes(job?.status) && dismissed.has(id)) {
          changed = true;
          continue;
        }
        next[id] = job;
      }
      return changed ? next : prev;
    });
  }, [dismissedRecentJobIds]);

  const upsertJob = useCallback((jobId, patch) => {
    if (!jobId) return;
    // Read the latest dismissed list from the ref so this callback can have
    // stable identity (empty deps) and not be re-bound on every dismiss.
    const dismissedNow = dismissedRecentJobIdsRef.current;
    if (
      !FINAL_STATUSES.includes(patch?.status) &&
      dismissedNow.includes(jobId)
    ) {
      // The job is back to a non-final state (e.g. user re-uploaded the same
      // file). Un-dismiss it so it shows up again.
      setDismissedRecentJobIds((prev) => prev.filter((id) => id !== jobId));
    }
    setJobs((prev) => {
      const nextJob = { ...(prev[jobId] || {}), jobId, ...patch };
      // Re-read at apply-time as well: a parallel dismiss action might have
      // updated the ref between the dismissed check above and the setJobs
      // commit.
      const dismissedAtCommit = dismissedRecentJobIdsRef.current;
      if (
        FINAL_STATUSES.includes(nextJob.status) &&
        dismissedAtCommit.includes(jobId)
      ) {
        if (!prev[jobId]) return prev;
        const next = { ...prev };
        delete next[jobId];
        return next;
      }
      return {
        ...prev,
        [jobId]: nextJob,
      };
    });
  }, []);

  const removeJob = useCallback((jobId) => {
    if (!jobId) return;
    setJobs((prev) => {
      const next = { ...prev };
      delete next[jobId];
      return next;
    });
    setActiveJobId((prev) => (prev === jobId ? null : prev));
  }, []);

  const clearFinishedJobs = useCallback(() => {
    setJobs((prev) => {
      const dismissedIds = Object.entries(prev)
        .filter(([, j]) => FINAL_STATUSES.includes(j?.status))
        .map(([id]) => id);
      if (dismissedIds.length > 0) {
        setDismissedRecentJobIds((old) =>
          Array.from(new Set([...old, ...dismissedIds])).slice(
            -MAX_DISMISSED_RECENT_JOBS,
          ),
        );
      }
      const next = {};
      for (const [id, j] of Object.entries(prev)) {
        if (!FINAL_STATUSES.includes(j?.status)) next[id] = j;
      }
      return next;
    });
  }, []);

  const dismissRecentJob = useCallback((jobId) => {
    if (!jobId) return;
    setDismissedRecentJobIds((prev) =>
      Array.from(new Set([...prev, jobId])).slice(-MAX_DISMISSED_RECENT_JOBS),
    );
    setJobs((prev) => {
      const next = { ...prev };
      delete next[jobId];
      return next;
    });
    setActiveJobId((prev) => (prev === jobId ? null : prev));
  }, []);

  const replaceJobsSnapshot = useCallback((list) => {
    if (!Array.isArray(list)) return;
    const map = {};
    // Use the ref so the latest dismissed list always wins — the SSE
    // consumer captures this callback once at mount, so closing over the
    // state directly would silently re-add jobs the user just dismissed.
    const dismissed = new Set(dismissedRecentJobIdsRef.current);
    for (const j of list) {
      if (!j || !j.job_id) continue;
      if (
        FINAL_STATUSES.includes(j.status) &&
        dismissed.has(j.job_id)
      ) {
        continue;
      }
      map[j.job_id] = normalizeServerJob(j);
    }
    setJobs(map);
  }, []);

  const clearTranscription = useCallback(() => {
    setJobs({});
    setActiveJobId(null);
    setTranscriptionData(null);
    setFile(null);
    setLoading(false);
    // SEC-037: dismissed-Liste auch zurücksetzen, damit nach Logout keine
    // Job-IDs des Vorgängers im Memory noch greifen können.
    setDismissedRecentJobIds([]);

    sessionStorage.removeItem(JOBS_STORAGE_KEY);
    sessionStorage.removeItem(LEGACY_JOB_STATE_KEY);
    sessionStorage.removeItem('transcriptionData');
    // SEC-037: localStorage-Persistenz für dismissed-Liste mit aufräumen.
    localStorage.removeItem(DISMISSED_RECENT_JOBS_KEY);
  }, []);

  // Legacy-Shape: manche Views lesen noch `jobState` (= der aktive Job).
  const jobState = activeJobId ? jobs[activeJobId] || {} : {};
  const setJobState = useCallback(
    (updater) => {
      setJobs((prev) => {
        const current = activeJobId ? prev[activeJobId] || {} : {};
        const next =
          typeof updater === 'function' ? updater(current) : updater || {};
        if (!next || !next.jobId) {
          // leerer State -> aktiven Job entfernen
          if (activeJobId) {
            const copy = { ...prev };
            delete copy[activeJobId];
            return copy;
          }
          return prev;
        }
        setActiveJobId(next.jobId);
        return { ...prev, [next.jobId]: next };
      });
    },
    [activeJobId],
  );

  return (
    <TranscriptionContext.Provider
      value={{
        jobs,
        setJobs,
        upsertJob,
        removeJob,
        dismissRecentJob,
        clearFinishedJobs,
        replaceJobsSnapshot,
        activeJobId,
        setActiveJobId,
        jobState,
        setJobState,
        transcriptionData,
        setTranscriptionData,
        clearTranscription,
        file,
        setFile,
        loading,
        setLoading,
      }}
    >
      {children}
    </TranscriptionContext.Provider>
  );
};

export function normalizeServerJob(serverJob) {
  if (!serverJob) return {};
  return {
    jobId: serverJob.job_id,
    status: serverJob.status,
    stage: serverJob.stage,
    stagePercent: serverJob.stage_percent || 0,
    overallPercent: serverJob.overall_percent || 0,
    queuePosition: serverJob.queue_position,
    audioDuration: serverJob.audio_duration,
    estimatedProcessingSeconds: serverJob.estimated_processing_seconds,
    estimatedStartSeconds: serverJob.estimated_start_seconds,
    estimatedStartAt: serverJob.estimated_start_at,
    estimatedFinishSeconds: serverJob.estimated_finish_seconds,
    estimatedFinishAt: serverJob.estimated_finish_at,
    processingStartedAt: serverJob.processing_started_at,
    gpuWaitStartedAt: serverJob.gpu_wait_started_at,
    gpuWaitElapsedSeconds: serverJob.gpu_wait_elapsed_seconds,
    gpuWaitDeadlineAt: serverJob.gpu_wait_deadline_at,
    gpuWaitRemainingSeconds: serverJob.gpu_wait_remaining_seconds,
    originalFilename: serverJob.original_filename,
    language: serverJob.language,
    attemptCount: serverJob.attempt_count,
    maxAttempts: serverJob.max_attempts,
    retrying: !!serverJob.retrying,
    retryReason: serverJob.retry_reason,
    error: serverJob.error,
    createdAt: serverJob.created_at,
    updatedAt: serverJob.updated_at,
    finishedAt: serverJob.finished_at,
  };
}
